import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Search, Command as CommandIcon } from "lucide-react";
import { cn } from "./cn";
import { Kbd } from "./Badge";

/**
 * Lightweight command palette. Open with cmd/ctrl+K.
 *
 * Props:
 *   - commands: [{ id, label, hint?, group?, keywords?, icon?, run() }]
 *   - placeholder?
 */
export function CommandPalette({ commands = [], placeholder = "Type a command or search…" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  // Global cmd-K hotkey
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return commands;
    return commands.filter((c) => {
      const hay = [
        c.label,
        c.hint,
        c.group,
        ...(c.keywords || []),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [commands, query]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((cmd) => {
      const g = cmd.group || "Actions";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(cmd);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const flat = filtered;
  const close = useCallback(() => setOpen(false), []);
  const runActive = useCallback(() => {
    const cmd = flat[active];
    if (!cmd) return;
    close();
    setTimeout(() => cmd.run?.(), 0);
  }, [flat, active, close]);

  useEffect(() => { setActive(0); }, [query]);

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(flat.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      runActive();
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[900] flex items-start justify-center pt-[12vh] px-4 animate-fade-in"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={close} />
      <div className="relative w-full max-w-xl rounded-2xl border border-border-strong bg-bg-surface shadow-card overflow-hidden animate-scale-in">
        <div className="flex items-center gap-2 border-b border-border px-3.5 py-2.5">
          <Search size={16} className="text-ink-subtle" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className="flex-1 bg-transparent text-sm text-ink placeholder:text-ink-subtle outline-none"
          />
          <Kbd>Esc</Kbd>
        </div>
        <div className="max-h-[50vh] overflow-y-auto">
          {flat.length === 0 ? (
            <div className="text-sm text-ink-muted px-4 py-6 text-center">No commands match.</div>
          ) : (
            groups.map(([group, items]) => (
              <div key={group} className="py-1.5">
                <div className="px-3 pt-1 pb-0.5 text-[10px] uppercase tracking-wider text-ink-subtle">{group}</div>
                {items.map((cmd) => {
                  const idx = flat.indexOf(cmd);
                  const isActive = idx === active;
                  return (
                    <button
                      key={cmd.id}
                      type="button"
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => { close(); setTimeout(() => cmd.run?.(), 0); }}
                      className={cn(
                        "w-full flex items-center gap-2.5 px-3 py-2 text-sm",
                        "transition-colors",
                        isActive ? "bg-brand-500/15 text-ink" : "text-ink-muted hover:text-ink hover:bg-white/[0.03]"
                      )}
                    >
                      <span className={cn("h-7 w-7 rounded-md flex items-center justify-center shrink-0",
                        isActive ? "bg-brand-500/20 text-brand-300" : "bg-bg-elevated text-ink-subtle")}>
                        {cmd.icon || <CommandIcon size={14} />}
                      </span>
                      <span className="flex-1 text-left truncate">{cmd.label}</span>
                      {cmd.hint && <span className="text-xs text-ink-subtle shrink-0">{cmd.hint}</span>}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="border-t border-border bg-bg-elevated/40 px-3 py-2 flex items-center justify-between text-[11px] text-ink-subtle">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Kbd>↵</Kbd> open</span>
            <span className="flex items-center gap-1"><Kbd>↑</Kbd><Kbd>↓</Kbd> navigate</span>
          </div>
          <span>{flat.length} {flat.length === 1 ? "result" : "results"}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Convenient hook to programmatically open the palette via custom event.
export function useOpenCommandPalette() {
  return useCallback(() => {
    window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }));
  }, []);
}
